import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, MaskLineInView } from "./Reveal";

const FAQS = [
  { q: "Was ist im monatlichen Preis enthalten?", a: "Website, Domain, Hosting, SSL sowie technische Wartung und Betreuung. Je nach Paket zusätzlich mehrere Seiten, WhatsApp-Integration, FAQ-Bereich, Referenzen und ein umfangreicherer Änderungsservice – alles aus einer Hand zu einem festen monatlichen Preis." },
  { q: "Sind Domain und Hosting inklusive?", a: "Ja. Domain und Hosting sind im Website-Service enthalten. Sie benötigen keinen eigenen Vertrag und müssen sich um nichts Technisches kümmern." },
  { q: "Gibt es hohe einmalige Erstellungskosten?", a: "Nein. Statt eines großen Betrags vorab zahlen Sie einen festen monatlichen Preis ab 49 € brutto – inklusive Erstellung, Domain, Hosting und Betreuung." },
  { q: "Wie unterscheiden sich Basic, Business und Premium?", a: "Basic ist der professionelle Einstieg mit einem Onepager. Business bietet bis zu 3 Seiten und mehr Funktionen wie WhatsApp, FAQ und Referenzen. Premium bietet bis zu 5 Seiten, die höchste Individualisierung und den erweiterten Änderungsservice." },
  { q: "Wie lange läuft der Vertrag?", a: "Die Mindestvertragslaufzeit beträgt 12 Monate. Danach verlängert sich der Vertrag jeweils nur um einen weiteren Monat." },
  { q: "Wie funktioniert die Kündigung?", a: "Die Kündigungsfrist beträgt 1 Monat zum Ende der jeweiligen Vertragslaufzeit." },
  { q: "Bin ich nach den ersten 12 Monaten wieder ein Jahr gebunden?", a: "Nein. Nach der 12-monatigen Mindestlaufzeit verlängert sich Ihr Website-Service lediglich um jeweils einen weiteren Monat – Sie bleiben flexibel." },
  { q: "Was passiert mit meiner Website nach einer Kündigung?", a: "Nach Ablauf des Vertrags wird Ihr Webauftritt offline genommen. Anschließend fallen keine weiteren monatlichen Gebühren an. Ihre Website bleibt bei uns gespeichert, sodass Sie sie jederzeit wieder aktivieren lassen können." },
  { q: "Kann ich meine Website später wieder aktivieren?", a: "Ja. Sie möchten später wieder online gehen? Ihr Webauftritt kann jederzeit wieder für Sie aktiviert werden – Sie starten dann einfach dort weiter, wo Sie aufgehört haben." },
  { q: "Kann ich mein Paket später wechseln?", a: "Ihr Unternehmen wächst? Ihr Webauftritt kann mitwachsen. Ein Wechsel in ein umfangreicheres Paket – zum Beispiel von Basic auf Business oder Premium – ist grundsätzlich möglich." },
  { q: "Was zählt als Änderung?", a: "Ein Änderungsauftrag ist eine zusammenhängende kleinere Aktualisierung Ihrer bestehenden Website – z. B. Texte anpassen, Bilder austauschen, Öffnungszeiten oder Kontaktdaten ändern, Referenzen oder Mitarbeiter aktualisieren. Nicht enthalten sind komplette Neugestaltungen, umfangreiche neue Seiten oder ein vollständiger Relaunch. Geht ein Wunsch darüber hinaus, informieren wir Sie vorher." },
  { q: "Kann Google Maps integriert werden?", a: "Ja. Ihr Unternehmensstandort kann direkt über Google Maps auf der Website eingebunden werden." },
  { q: "Gibt es ein Kontaktformular?", a: "Ja. Ein Kontaktformular ist in allen Paketen enthalten, damit Interessenten Sie direkt über die Website erreichen." },
  { q: "Kann WhatsApp integriert werden?", a: "Ja, ab dem Business-Paket. So können Interessenten Sie mit einem Klick per WhatsApp kontaktieren." },
  { q: "Ist die Website für Smartphones optimiert?", a: "Ja. Alle Websites werden für Smartphone, Tablet und Desktop optimiert – gerade weil viele Besucher über das Handy kommen." },
  { q: "Kann ich meine bestehende Domain verwenden?", a: "In der Regel lässt sich eine bestehende Domain verwenden. Die Details klären wir individuell mit Ihnen." },
  { q: "Für welche Unternehmen eignet sich der Website-Service?", a: "Für kleine und mittelständische Unternehmen, lokale Dienstleister, Handwerksbetriebe, Selbstständige, Coaches und Existenzgründer, die einen professionellen Webauftritt ohne hohen eigenen Aufwand möchten." },
];

function Item({ f, i, open, setOpen }) {
  const isOpen = open === i;
  return (
    <button
      onClick={() => setOpen(isOpen ? -1 : i)}
      className="w-full text-left border-t border-white/10 py-6 flex items-start justify-between gap-6 group"
      data-testid={`faq-${i}`}
    >
      <div className="flex-1">
        <div className="flex items-baseline gap-4">
          <span className="font-tech text-xs text-[#FF3B00]">{String(i + 1).padStart(2, "0")}</span>
          <span className="font-display text-lg md:text-xl text-white group-hover:text-[#FF3B00] transition-colors">
            {f.q}
          </span>
        </div>
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.p
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden text-neutral-400 text-sm leading-relaxed max-w-2xl pl-9"
            >
              <span className="block pt-4">{f.a}</span>
            </motion.p>
          )}
        </AnimatePresence>
      </div>
      <Plus
        size={22}
        className={`shrink-0 text-neutral-500 mt-1 transition-transform duration-300 ${isOpen ? "rotate-45 text-[#FF3B00]" : ""}`}
      />
    </button>
  );
}

export default function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="relative py-14 lg:py-20 border-t border-white/10 bg-[#0b0806]/70" data-testid="faq">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-[0.75fr_1.25fr] gap-10">
        <div>
          <Reveal className="font-tech text-xs uppercase tracking-[0.25em] text-[#FF3B00] mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-[#FF3B00]" /> Fragen & Antworten
          </Reveal>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight text-white uppercase lg:sticky lg:top-28">
            <MaskLineInView>Häufige</MaskLineInView>
            <MaskLineInView delay={0.1}>Fragen</MaskLineInView>
          </h2>
        </div>
        <div className="border-b border-white/10">
          {FAQS.map((f, i) => (
            <Item key={f.q} f={f} i={i} open={open} setOpen={setOpen} />
          ))}
        </div>
      </div>
    </section>
  );
}
